import React, { useContext, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import moment from 'moment';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
// Context
import useCallPerformanceContext from '../context/useCallPerformanceContext';
// Api
import { getManagerFilter, getRepFilter } from '../../../api/dropdownOptionsApi';
import { postCallRateBreakdown, postCallRateExport } from '../../../api/crmDashboard';

export default function CallRateModal() {
  const { filterForm, callRateModal, setCallRateModal } = useContext(useCallPerformanceContext);

  const modalFilterFormDefault = {
    manager_id: { label: 'ALL', value: null },
    rep_id: filterForm?.rep_id,
    date_from: filterForm?.date_from,
    date_to: filterForm?.date_to,
  };

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState({ plotted: 0, actual: 0, duesoon: 0, overdue: 0 });
  const [modalFilterForm, setModalFilterForm] = useState(modalFilterFormDefault);
  const [managerOptions, setManagerOptions] = useState([]);
  const [repOptions, setRepOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [sortModel, setSortModel] = useState([
    {
      field: 'id',
      sort: 'desc',
    },
  ]);

  const columns = [
    {
      field: 'nsm_name',
      headerName: 'NSM Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'manager_name',
      headerName: 'Manager Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'mpr_name',
      headerName: 'MPR Name',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'md_name',
      headerName: 'MD',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.md_name,
    },
    {
      field: 'date_scheduled',
      headerName: 'Date Schedule',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.date_scheduled,
    },
    {
      field: 'date_covered',
      headerName: 'Date Covered',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.date_covered,
    },
    {
      field: 'plotted',
      headerName: 'Plotted',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.plotted),
    },
    {
      field: 'actual',
      headerName: 'Actual',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) =>
        Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(row.actual),
    },
    {
      field: 'performance_percentage',
      headerName: 'Perf %',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => `${row.performance_percentage}%`,
    },
    {
      field: 'coverage_status',
      headerName: 'Status Coverage',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
      renderCell: ({ row }) => row.coverage_status,
    },
  ];

  const handleCancel = () => {
    setCallRateModal(false);
  };

  const loadManagersDropdown = async () => {
    const request = {
      date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
      date_to: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
    };
    const response = await getManagerFilter(request);
    const { code, data } = response.data;

    if (code === '200') {
      const dropdownValue = data?.map((row) => ({
        value: row.manager_id,
        label: row.manager_name,
      }));
      setManagerOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const loadRepDropdown = async () => {
    const request = {
      manager_id: modalFilterForm?.manager_id?.value,
      date_from: modalFilterForm?.date_from,
      date_to: modalFilterForm?.date_from,
    };
    const reponse = await getRepFilter(request);
    const { code, data } = reponse.data;
    if (code === '200') {
      const dropdownValue = data?.map((row) => ({
        value: row.rep_id,
        label: row.rep_name,
      }));
      setRepOptions([{ label: 'ALL', value: null }, ...dropdownValue]);
    }
  };

  const handleGenerateFilter = async (clear = null) => {
    try {
      setLoading(true);
      let request;
      if (clear === 'reset') {
        request = {
          team: filterForm?.team?.value,
          manager_id: modalFilterFormDefault?.manager_id?.value,
          mpr: modalFilterFormDefault?.rep_id?.value,
          date_from: moment(modalFilterFormDefault?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterFormDefault?.date_to).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          team: filterForm?.team?.value,
          manager_id: modalFilterForm?.manager_id?.value,
          mpr: modalFilterForm?.rep_id?.value,
          date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
          date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
        };
      }

      const response = await postCallRateBreakdown(request);
      const { code, actual, plotted, planned, retrieved, advanced, missed, pending, data } =
        response.data;

      if (code === '200') {
        setTotal({
          ...total,
          plotted: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(plotted),
          actual: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(actual),
          planned: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(planned),
          retrieved: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(retrieved),
          advanced: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(advanced),
          missed: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(missed),
          pending: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(pending),
        });
        setTableData(data);
        setLoading(false);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeFilter = (e, row, name) => {
    setModalFilterForm({
      ...modalFilterForm,
      [name]: row,
    });
  };

  const handleClearFilter = () => {
    setModalFilterForm(modalFilterFormDefault);
    handleGenerateFilter('reset');
  };

  const handleExportBreakdown = async () => {
    try {
      toast.loading('Please wait ...');

      const request = {
        team: filterForm?.team?.value,
        manager_id: modalFilterForm?.manager_id?.value,
        rep_id: modalFilterForm?.rep_id?.value,
        date_from: moment(modalFilterForm?.date_from).format('YYYY-MM-DD'),
        date_to: moment(modalFilterForm?.date_to).format('YYYY-MM-DD'),
      };

      const res = await postCallRateExport(request);

      const { code, result } = res.data.response;
      if (code === '200') {
        window.location.href = result;
        toast.success(`Successfully downloaded file: ${res?.data?.response?.file_name}`);
      }
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const pinnedRows = {
    top: tableData?.length ? [tableData[0]] : [],
  };

  useEffect(() => {
    loadManagersDropdown();
    loadRepDropdown();
  }, [modalFilterForm]);

  useEffect(() => {
    handleGenerateFilter();
  }, []);

  return (
    <div>
      <Dialog open={callRateModal} onClose={handleCancel} maxWidth='auto' fullWidth>
        <DialogTitle>
          Call Rate
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Autocomplete
                disablePortal
                id='manager_id'
                disableClearable
                fullWidth
                options={managerOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='Manager' />}
                size='small'
                value={modalFilterForm?.manager_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'manager_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Autocomplete
                disablePortal
                id='rep_id'
                disableClearable
                fullWidth
                options={repOptions}
                // eslint-disable-next-line react/jsx-props-no-spreading
                renderInput={(params) => <TextField {...params} label='MPR' />}
                size='small'
                value={modalFilterForm?.rep_id}
                onChange={(e, newValue) => handleChangeFilter(e, newValue, 'rep_id')}
                sx={{ backgroundColor: '#fff' }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2} lg={2}>
              <Button variant='contained' color='success' onClick={handleGenerateFilter}>
                Generate
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleClearFilter()}
                sx={{ marginLeft: '10px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6} md={6} lg={6} pl={3} pt={3}>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                Plotted: {total.plotted}
                <span style={{ marginLeft: '50px', color: '#14A44D' }}>Actual: {total.actual}</span>
                <span style={{ marginLeft: '50px' }}>Planned: {total.planned}</span>
                <span style={{ marginLeft: '50px' }}>Advanced: {total.advanced}</span>
                <span style={{ marginLeft: '50px' }}>Retrieved: {total.retrieved}</span>
                <span style={{ marginLeft: '50px' }}>Missed: {total.missed}</span>
                <span style={{ marginLeft: '50px' }}>Pending: {total.pending}</span>
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }} />
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6} pl={3} pt={3}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant='contained'
                  color='warning'
                  onClick={() => handleExportBreakdown()}
                  startIcon={<CloudDownloadIcon />}
                  sx={{
                    marginLeft: '10px',
                    paddingTop: '10px',
                  }}
                >
                  Export
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} pl={2} pt={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Box
                className='table-container'
                sx={{
                  height: `600px`,
                  '& .super-app-theme--header': {
                    fontWeight: 'bold',
                    backgroundColor: '#808080',
                    color: '#fff',
                  },
                  '& .MuiDataGrid-pinnedRows': { fontWeight: 'bold', backgroundColor: '#FBCEB1' },
                }}
              >
                <DataGridPro
                  loading={loading}
                  rows={tableData}
                  columns={columns}
                  sortModel={sortModel}
                  experimentalFeatures={{ rowPinning: true }}
                  pinnedRows={pinnedRows}
                  initialState={{
                    ...tableData.initialState,
                    pagination: {
                      pageSize: 50,
                    },
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  pagination
                  onSortModelChange={(model) => setSortModel(model)}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
